/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2019, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS 'AS IS' AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router-dom'
import moment from 'moment'
import * as Sentry from '@sentry/browser'
import { isEqual, isEmpty } from 'lodash'
import qs from 'query-string'

import {
  DateRange,
  Select,
  Button,
  SvgIcon
} from '../'

import {
  MetricTable
} from './MetricTable'

import {
  hasDateChanged,
  datesInRangeFrequency,
  decodeBase64,
  runQuery,
  upperCaseFirstCharacter
} from '../../utils'


const getInitialState = ({ startDate, endDate, frequency, location }) => {
  const dateRange = datesInRangeFrequency(startDate, endDate, frequency)

  let defaultState = {
    startDate,
    endDate,
    frequency,
    metricResults: [],
    metricFamilyToCompare: 'all',
    metricGroupToCompare: 'all',
    metricToCompare: 'all',
    inputValuesChangedAfterTableRender: null,
    renderTimestamp: null,
    dateRange,
    metricFamilyOptions: null,
    metricGroupOptions: null,
    metricOptions: null,
    lastDateInRangeWithMetricResult: moment(dateRange[dateRange.length - 1]),
    dataBeingFetched: false
  }

  let queryParams = qs.parse(location.search)
  let numberOfQueryParams = Object.keys(queryParams).length
  if (numberOfQueryParams > 1) {  // expecting tab url param to always be present for global-level metric comparison; it gets set in parent component
    // if initially url was entered, or copy-and-pasted, with filters specified in url search params, set state accordingly

    const filters = getFiltersFromQueryString(location.search)

    if (['startDate', 'endDate', 'frequency'].some(key => filters.hasOwnProperty(key))) {  // if any of any of 'startDate', 'endDate' or 'frequency' is found in filters
      const { startDate, endDate, frequency } = { ...defaultState, ...filters }  // get 'latest' values across defaultState and filters objects (filter values will replace the corresponding value on defaulState)
      const dateRange = datesInRangeFrequency(startDate, endDate, frequency)
      filters.dateRange = dateRange  // include dateRange in initialisation
    }

    return { ...defaultState, ...filters }
  } else {
    return defaultState
  }
}

const getFiltersFromQueryString = (queryParamsString) => {
  let queryParams = qs.parse(queryParamsString)

  let filters = Object.assign(
    {},
    ...Object.keys(queryParams).map(key => (
      ['family', 'group', 'metric'].indexOf(key) > -1
       ? { [`${key === 'metric' ? 'metric' : `metric${upperCaseFirstCharacter(key)}`}ToCompare`]: queryParams[key] }
       : [ 'startDate', 'endDate' ].indexOf(key) > -1
         ? { [key]: moment(queryParams[key], 'YYYYMMDDTHHmm') }
         : { [key]: queryParams[key] }
    ))
  )

  return filters
}

const fetchMetricsAcrossDateRange = (objectId, identityId, metricSourceId, dateRange, renderTemplate) => {
  let parameters = [
    { name: 'metricsource_Id', type: 'UUID!', value: decodeBase64(metricSourceId) },
    { name: 'createdAt_Gte', type: 'DateTime!', value: `${moment(dateRange[0], 'DD/MM/YYYY').format('YYYY-MM-DD')}T00:00:00`},
    { name: 'createdAt_Lte', type: 'DateTime!', value: `${moment(dateRange[dateRange.length -1], 'DD/MM/YYYY').format('YYYY-MM-DD')}T23:59:59`},
    { name: 'orderBy', type: 'String!', value: 'created_at' }
  ]

  if (objectId) {
    parameters.push({ name: 'relations_ObjectId', type: 'UUID', value: decodeBase64(objectId) })
  }

  if (identityId) {
    parameters.push({ name: 'identity_Id', type: 'UUID', value: decodeBase64(identityId) })
  }

  return runQuery(
      // model
      'metrics',
      // parameters
      parameters,
      null,
      // literal field string to return
      `edges {
        node {
          id
          result
          createdAt
        }
      }`
    )
    .then(({ data }) => {
      let metricFamilyOptions = []
      let lastDateInRangeWithMetricResult = null
      let metricResults = []

      data.metrics.edges.forEach(({ node: metric }) => {
        let result = JSON.parse(metric.result)
        metricResults.push({ result, date: metric.createdAt })

        let createdAt = moment(metric.createdAt)
        if (!isEmpty(result) && dateRange.includes(createdAt.format('DD/MM/YYYY'))) {
          lastDateInRangeWithMetricResult = createdAt
        }
        let options = Object.keys(result)
        options.forEach(opt => {
          // options populating 'compare' drop-down is limited to what is specified in render tamplate
          if (metricFamilyOptions.includes(opt) === false && renderTemplate.hasOwnProperty(opt)) {
            metricFamilyOptions.push(opt)
          }
        })
      })

      return [ metricFamilyOptions, metricResults, lastDateInRangeWithMetricResult ]
    })
    .catch(error => {
      Sentry.captureMessage('Failed to fetch all metric options across date range', 'error')
      return [ [], [], null ]
    }
  )
}

// HOW THIS COMPONENT WORKS
//  For the combination of props passed in, a metric comparison table will be generated and displayed.
//  Defaults 'startDate' to a month ago today, 'endDate' to today and 'frequency' of comparison results displayed as monthly.
//
//  According to the inputs, a date range will be generated that will correspond to columns displayed in results table.
//  The available input metric options and row keys in the results table will be determined considering the generated date range.
//  The latest date with metrics will be found and used to populate input metric options and row keys for results to be displayed.
//
//  If data for a specific date is not found a blank column will be displayed.
//  If data for a specific metric key is not found a blank cell will be displayed.

class MetricComparisonTable extends React.PureComponent {
  static propTypes = {
    objectId: PropTypes.string,
    identityId: PropTypes.string,
    metricSourceId: PropTypes.string.isRequired,
    renderTemplate: PropTypes.object.isRequired,
    startDate: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(moment)
    ]),
    endDate: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(moment)
    ]),
    frequency: PropTypes.string,
    className: PropTypes.string
  }

  static defaultProps = {
    startDate: moment().subtract(1, 'months'),
    endDate: moment(),
    frequency: 'month',
    className: ''
  }

  state = getInitialState(this.props)

  componentDidMount() {
    let queryParams = qs.parse(this.props.location.search)
    let numberOfQueryParams = Object.keys(queryParams).length

    if (numberOfQueryParams === 0 || (numberOfQueryParams === 1 && queryParams.tab)) {  // expecting tab url param to always be present for global-level metric comparison; it gets set in parent component
      this.setQueryString(true)
      this.initMetricsData()
    } else {
      this.initMetricsData()
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.location.search !== this.props.location.search) {
      this.setState(getFiltersFromQueryString(this.props.location.search))
    } else if (hasDateChanged(prevState.startDate, this.state.startDate) ||
        hasDateChanged(prevState.endDate, this.state.endDate) ||
        prevState.frequency !== this.state.frequency) {

      let stateChange = { dataBeingFetched: false }
      let dateRange = datesInRangeFrequency(this.state.startDate, this.state.endDate, this.state.frequency)
      Object.assign(stateChange, { dateRange })
      this.setState({ dataBeingFetched: true })

      fetchMetricsAcrossDateRange(this.props.objectId, this.props.identityId, this.props.metricSourceId, dateRange, this.props.renderTemplate)
        .then(([metricFamilyOptions, metricResults, lastDateInRangeWithMetricResult]) => {
          if (!isEqual(dateRange, this.state.dateRange)) {
            Object.assign(stateChange, { metricFamilyOptions, metricResults })
            if (lastDateInRangeWithMetricResult && !lastDateInRangeWithMetricResult.isSame(this.state.lastDateInRangeWithMetricResult)) {
              Object.assign(stateChange, { lastDateInRangeWithMetricResult })
            }
          }
          this.setState(stateChange)
        }
      )
    }
  }

  initMetricsData = () => {
    fetchMetricsAcrossDateRange(this.props.objectId, this.props.identityId, this.props.metricSourceId, this.state.dateRange, this.props.renderTemplate)
    .then(([metricFamilyOptions, metricResults, lastDateInRangeWithMetricResult]) => {
      let stateChange = { renderTimestamp: moment().unix() } // moment().unix() gets Unix timestamp in seconds

      if (lastDateInRangeWithMetricResult) {
        Object.assign(stateChange, { metricFamilyOptions, lastDateInRangeWithMetricResult, metricResults })
      } else {
        Object.assign(stateChange, { metricFamilyOptions, metricResults })
      }

      this.setState(stateChange)
    })
  }

  setQueryString = () => {
    let { startDate, endDate, frequency, metricFamilyToCompare, metricGroupToCompare, metricToCompare } = this.state
    let filters = {
      startDate: startDate.format('YYYYMMDDTHHmm'),
      endDate: endDate.format('YYYYMMDDTHHmm'),
      frequency,
      family: metricFamilyToCompare,
      group: metricGroupToCompare,
      metric: metricToCompare
    }

    if (Object.keys(filters).length > 0) {
      let { startDate, endDate, frequency, family, group, metric, ...otherQueryParams } = qs.parse(this.props.location.search)
      this.props.history.push({ search: qs.stringify({ ...filters, ...otherQueryParams }) })
    }
  }

  changeState = (e) => {
    if (e.target.id === 'metricFamilyToCompare') {
      this.setState({
        [e.target.id]: e.target.value || 'all',  // if 'id' on components matches corresponding state variable names, this is how state can be set
        inputValuesChangedAfterTableRender: true,
        metricGroupToCompare: 'all'
      })
    } else if (e.target.id === 'metricGroupToCompare') {
      this.setState({
        [e.target.id]: e.target.value || 'all',
        inputValuesChangedAfterTableRender: true,
        metricToCompare: 'all'
      })
    } else if (e.target.id === 'metricToCompare') {
      this.setState({
        [e.target.id]: e.target.value || 'all',
        inputValuesChangedAfterTableRender: true
      })
    } else {
      this.setState({
        [e.target.id]: e.target.value,
        inputValuesChangedAfterTableRender: true
      })
    }
  }

  changeStartDate = (date) => (
    this.setState({
      startDate: date,
      inputValuesChangedAfterTableRender: true
    })
  )
  changeEndDate = (date) => (
    this.setState({
      endDate: date,
      inputValuesChangedAfterTableRender: true
    })
  )

  updateRenderTimeStamp = () => this.setState({
    renderTimestamp: moment().unix(),
    inputValuesChangedAfterTableRender: false
  }, this.setQueryString)

  requiredValuesEntered = () => {
    let { startDate, endDate, frequency, metricFamilyToCompare, metricGroupToCompare, metricToCompare } = this.state
    return startDate !== null && endDate !== null && frequency &&
      metricFamilyToCompare && metricGroupToCompare && metricToCompare
  }

  render() {
    let { objectId, metricSourceId, className, renderTemplate } = this.props
    let { startDate, endDate, frequency, dateRange, metricFamilyToCompare, metricFamilyOptions, metricResults,
      inputValuesChangedAfterTableRender, renderTimestamp, metricGroupToCompare, metricToCompare, dataBeingFetched } = this.state

    let metricDateOffsetIndex = dateRange.indexOf(this.state.lastDateInRangeWithMetricResult.format('DD/MM/YYYY'))  // used to populate first column key in results table
    let metricGroupOptions = []
    let metricOptions = []
    let latestMetrics = null

    if (metricResults.length > 0) {
      latestMetrics = metricResults.find(result => {
        let latestDateWithMetrics = metricDateOffsetIndex > -1
          ? moment(dateRange[metricDateOffsetIndex], 'DD/MM/YYYY')
          : moment(dateRange[dateRange.length - 1], 'DD/MM/YYYY')
        return moment(result.date).isSame(latestDateWithMetrics, 'day')
      })
      if (latestMetrics) {
        if (latestMetrics.result.hasOwnProperty(metricFamilyToCompare)) {
          let metricFamily = latestMetrics.result[metricFamilyToCompare]
          if (typeof metricFamily === 'object') {
            metricGroupOptions = [...metricGroupOptions, ...Object.keys(metricFamily)]
            if (metricGroupOptions.length === 1) {
              metricGroupToCompare = metricGroupOptions[0]
            }
            if (metricGroupToCompare && metricFamily.hasOwnProperty(metricGroupToCompare)) {
              let metricGroup = metricFamily[metricGroupToCompare]
              if (typeof metricGroup === 'object') {
                metricOptions = [ ...metricOptions, ...Object.keys(metricGroup)]
                if (metricOptions.length === 1) {
                  metricToCompare = metricOptions[0]
                }
              }
              else {
                metricToCompare = metricGroupToCompare
              }
            }
          }
        }
      }
    }

    return (
      <div className={className}>
        <div className='slds-m-bottom_x-large'>
          <div className='slds-grid slds-wrap slds-m-bottom_medium'>
            <div className='slds-grid slds-m-top_small slds-m-right_medium'>
              <span className='u-font-size-larger slds-m-top_xx-small slds-m-right_x-small'>
                For
              </span>
              <DateRange
                separatorText='to'
                startDate={startDate}
                endDate={endDate}
                onStartDateChange={this.changeStartDate}
                onEndDateChange={this.changeEndDate}
                disableEndDateInputWhenNoStartDate={true}
                showMonthDropdown={true}
                showYearDropdown={true}
                peekNextMonth={true}
                maxDate={moment()}  // today
                startDateClearable={false}
                endDateClearable={false}
              />
            </div>
            <div className='slds-grid slds-m-top_small slds-m-right_medium'>
              <span className='u-font-size-larger slds-m-top_xx-small slds-m-right_x-small'>
                every
              </span>
              <Select
                id='frequency'
                value={frequency}
                options={['day', 'week', 'month']}
                onChange={this.changeState}
              />
            </div>
          </div>
          <div className='slds-grid slds-m-top_small slds-m-right_x-large'>
            <span className='u-font-size-larger slds-m-top_xx-small slds-m-right_x-small'>
              compare
            </span>
            {metricFamilyOptions === null
              ? <Select
                  id='metricFamilyToCompare_loading'
                  placeholder='Loading metric family options'
                  value={null}
                  options={[]}
                  disabled={true}
                  style={{ width: 200 }}
                />
              : metricFamilyOptions.length > 0
                ? <React.Fragment>
                    <Select
                      id='metricFamilyToCompare'
                      placeholder='All'
                      default={metricFamilyOptions.length > 1 ? 'All' : null}
                      value={metricFamilyToCompare}
                      options={metricFamilyOptions}
                      onChange={this.changeState}
                      style={{ width: 200 }}
                    />
                    {metricFamilyToCompare && metricGroupOptions.length > 0 &&
                      <React.Fragment>
                        <Select
                          id='metricGroupToCompare'
                          placeholder='All'
                          value={metricGroupOptions.length > 1
                            ? metricGroupToCompare
                            : metricGroupOptions[0]  // if there is only 1 metric option to choose from, auto-select
                          }
                          default={metricGroupOptions.length > 1 ? 'All' : null}
                          options={metricGroupOptions}
                          onChange={this.changeState}
                          disabled={metricFamilyToCompare === null ||
                            (metricGroupOptions.length === 1 &&
                              latestMetrics &&
                              typeof latestMetrics.result[metricFamilyToCompare][metricGroupOptions[0]] !== 'object'
                            )
                          }
                          style={{ width: 200 }}
                        />
                        {metricOptions.length > 0 &&
                          <Select
                            id='metricToCompare'
                            placeholder='All'
                            value={metricOptions.length > 1
                              ? metricToCompare
                              : metricOptions[0]  // if there is only 1 metric option to choose from, auto-select
                            }
                            default={metricOptions.length > 1 ? 'All' : null}
                            options={metricOptions}
                            onChange={this.changeState}
                            disabled={metricFamilyToCompare === null || metricOptions.length === 1}
                            style={{ width: 200 }}
                          />
                        }
                      </React.Fragment>
                    }
                  </React.Fragment>
                : <Select
                    id='metricFamilyToCompare_error'
                    placeholder='Change date range'
                    value={null}
                    options={[]}
                    disabled={true}
                    style={{ width: 200 }}
                    hasError={true}
                  />
            }
          </div>
          <Button
            type='brand'
            onClick={this.updateRenderTimeStamp}
            minWidth={150}
            disabled={!this.requiredValuesEntered() || !inputValuesChangedAfterTableRender || dataBeingFetched}
            className='slds-m-top_small'>
            {dataBeingFetched
              ? <SvgIcon
                  variant='utility'
                  symbol='refresh'
                  size='x-small'
                  buttonIcon={true}
                  className='slds-button__icon_left'
                  rotateDegrees={360}
                  rotateTransitionDuration={0.5}
                />
              : <SvgIcon
                  variant='utility'
                  symbol='refresh'
                  size='x-small'
                  buttonIcon={true}
                  colour='inverse'
                  className='slds-button__icon_left'
                />
            }
            Refresh
          </Button>
        </div>
        <MetricTable
          objectId={objectId}
          metricSourceId={metricSourceId}
          latestMetrics={latestMetrics}
          metricFamilyToCompare={metricFamilyToCompare}
          metricGroupToCompare={metricGroupOptions.length === 1
            ? metricGroupOptions[0]
            : metricGroupToCompare
          }
          metricToCompare={metricOptions.length === 1
            ? metricOptions[0]
            : metricToCompare
          }
          metricResults={metricResults}
          dateRange={dateRange}
          renderTimestamp={renderTimestamp}
          availableMetricDateIndex={metricDateOffsetIndex}
          renderTemplate={renderTemplate}
        />
      </div>
    )
  }
}

export default withRouter(MetricComparisonTable)



// WEBPACK FOOTER //
// ./src/components/metric-comparison-table/index.js